import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import BookingModal from '../components/Modals/BookingModal';
import { bookingService } from '../services/bookingService';
import { adminService } from '../services/adminService';
import { getImagePath } from '../utils/imagePath';
import './FacilitiesPage.css';

const FacilitiesPage = ({ 
  facilities = [], 
  user = null, 
  setUser,
  addNotification = () => {}, 
  addAuditLog = () => {}, 
  generateBookingId = () => `RA-${Date.now()}`,
  equipmentByCategory: propsEquipmentByCategory = {} 
}) => {
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingStep, setBookingStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  
  // Equipment data state - fetched from SQL via PUBLIC endpoint
  const [equipmentByCategory, setEquipmentByCategory] = useState(propsEquipmentByCategory);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  const [equipmentError, setEquipmentError] = useState(null);
  
  // Dependent member states
  const [isDependent, setIsDependent] = useState(false);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [approvalFile, setApprovalFile] = useState(null);
  const [approvalFileName, setApprovalFileName] = useState('');
  
const [bookingData, setBookingData] = useState({
  date: '',
  endDate: '',        // ADD THIS - for multi-day events
  startTime: '08:00',
  endTime: '18:00',
  purpose: '',        // ADD THIS - purpose of booking
  guests: 1,
  notes: '',
  equipment: [],
  eventType: 'private',
  residentialArea: false,
  noiseAfter9pm: false,
  commitmentForm: null,
  clearanceFile: null,
  isSportingActivity: false,
  phone: user?.phone || '',
  whatsapp: user?.whatsApp || ''
});

  const [selectedCategory, setSelectedCategory] = useState('all');

  // PUBLIC function to fetch equipment from SQL (NO AUTH REQUIRED)
  const fetchPublicEquipment = async () => {
    const API_BASE_URL = process.env.NODE_ENV === 'development' ? '/api' : 'https://nlngraclub.com/nlnapi';
    
    setLoadingEquipment(true);
    setEquipmentError(null);
    
    try {
      console.log('📡 FacilitiesPage: Fetching equipment from SQL via PUBLIC endpoint...');
      
      // PUBLIC endpoint - NO Authorization header needed!
      const response = await fetch(`${API_BASE_URL}/equipment.php?action=list&_t=${Date.now()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
          // IMPORTANT: Do NOT include Authorization header here
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📦 Equipment API Response:', data);
      
      if (data.success && data.equipment) {
        const equipment = data.equipment;
        
        // Group equipment by category
        const grouped = equipment.reduce((acc, item) => {
          const category = item.category || 'Uncategorized';
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(item);
          return acc;
        }, {});
        
        setEquipmentByCategory(grouped);
        console.log(`✅ Loaded ${equipment.length} equipment items from SQL`);
        console.log(`📊 Categories: ${Object.keys(grouped).join(', ')}`);
      } else {
        console.warn('⚠️ No equipment data received or API returned error:', data);
        setEquipmentError(data.error || 'No equipment data received');
        setEquipmentByCategory({});
      }
    } catch (error) {
      console.error('❌ Error fetching equipment:', error);
      setEquipmentError(error.message);
      setEquipmentByCategory({});
    } finally {
      setLoadingEquipment(false);
    }
  };

  // Fetch equipment from SQL if not provided as prop
  useEffect(() => {
    // If no equipment data from props, fetch from SQL via public endpoint
    if (!propsEquipmentByCategory || Object.keys(propsEquipmentByCategory).length === 0) {
      fetchPublicEquipment();
    } else {
      setEquipmentByCategory(propsEquipmentByCategory);
    }
  }, [propsEquipmentByCategory]);

  // Check if user is a dependent who needs approval
  useEffect(() => {
    if (user) {
      // Full member with full_member category - NO approval needed
      const isFullMember = user.membership_type === 'primary' && user.membership_category === 'full_member';
      
      // Spouse of full member - NO approval needed
      const isSpouse = user.membership_type === 'dependent' && user.relationship_type === 'spouse';
      
      // All other dependents need approval
      const needsApproval = user.membership_type === 'dependent' && !isSpouse;
      
      setIsDependent(needsApproval);
      setRequiresApproval(needsApproval);
      
      if (needsApproval) {
        console.log('⚠️ Dependent member detected - approval file required for bookings');
      }
    }
  }, [user]);

  // Map facility names to image paths
  const getFacilityImage = (facility) => {
    const facilityName = facility.name?.toLowerCase() || '';
    const facilityCategory = facility.category?.toLowerCase() || '';
    
    const imageMap = {
      'abuja boulevard': getImagePath('images/bars/abuja-boulevard.jpg'),
      'banquet hall': getImagePath('images/bars/banquet-hall.jpg'),
      'clubhouse alcove': getImagePath('images/bars/clubhouse-alcove.jpg'),
      'clubhouse west-bar': getImagePath('images/bars/clubhouse-west-bar.jpg'),
      'clubhouse west bar': getImagePath('images/bars/clubhouse-west-bar.jpg'),
      'dbn bar': getImagePath('images/bars/dbn-bar.jpg'),
      'finima nature park': getImagePath('images/bars/finima-nature-park.jpg'),
      'golf course bar': getImagePath('images/bars/golf-course-bar.jpg'),
      'hippo bar': getImagePath('images/bars/hippo-bar.jpg'),
      'marquee tent': getImagePath('images/bars/marquee-tent.jpg'),
      'market square': getImagePath('images/bars/market-square.jpg'),
      'sports hall complex': getImagePath('images/company/sports-hall-complex.jpg'),
      'swimming pool': getImagePath('images/bars/swimming-pool.jpg'),
      'tennis hut': getImagePath('images/bars/tennis-hut.jpg'),
      'wellness center': getImagePath('images/company/wellness.jpg'),
      'kids play area': getImagePath('images/company/fun.PNG'),
    };
    
    if (imageMap[facilityName]) {
      return imageMap[facilityName];
    }
    
    for (const [key, value] of Object.entries(imageMap)) {
      if (facilityName.includes(key) || key.includes(facilityName)) {
        return value;
      }
    }
    
    const categoryImages = {
      'dining': getImagePath('images/bars/abuja-boulevard.jpg'),
      'events': getImagePath('images/company/xmas.jpg'),
      'sports': getImagePath('images/company/sports-hall-complex.jpg'),
      'bar': getImagePath('images/bars/hippo-bar.jpg'),
      'lounge': getImagePath('images/bars/clubhouse-alcove.jpg'),
      'outdoor': getImagePath('images/bars/finima-nature-park.jpg'),
      'shopping': getImagePath('images/bars/market-square.jpg'),
      'wellness': getImagePath('images/company/wellness.jpg'),
      'family': getImagePath('images/company/fun.PNG'),
    };
    
    return categoryImages[facilityCategory] || getImagePath('images/company/company-image.jpg');
  };

  const handleImageError = (facilityId) => {
    setImageErrors(prev => ({ ...prev, [facilityId]: true }));
  };

  const getFallbackImage = (category) => {
    const fallbacks = {
      'Dining': getImagePath('images/bars/abuja-boulevard.jpg'),
      'Events': getImagePath('images/company/xmas.jpg'),
      'Sports': getImagePath('images/company/sports-hall-complex.jpg'),
      'Bar': getImagePath('images/bars/hippo-bar.jpg'),
      'Lounge': getImagePath('images/bars/clubhouse-alcove.jpg'),
      'Outdoor': getImagePath('images/bars/finima-nature-park.jpg'),
      'Shopping': getImagePath('images/bars/market-square.jpg'),
      'Wellness': getImagePath('images/company/wellness.jpg'),
      'Family': getImagePath('images/company/fun.PNG'),
      'default': getImagePath('images/company/company-image.jpg')
    };
    return fallbacks[category] || fallbacks.default;
  };

  const allCategories = ['all', ...new Set(facilities.map(f => f.category).filter(Boolean))];
  const filteredFacilities = selectedCategory === 'all' 
    ? facilities 
    : facilities.filter(f => f.category === selectedCategory);

const handleBookNow = (facility) => {
  // Reset approval file when opening new booking
  setApprovalFile(null);
  setApprovalFileName('');
  
  if (facility?.sportingOnly) {
    setBookingData({
      ...bookingData,
      eventType: 'sporting',
      isSportingActivity: true,
      date: '',
      endDate: '',           // ADD THIS
      purpose: '',           // ADD THIS
      startTime: '08:00',
      endTime: '18:00',
      guests: 1,
      notes: '',
      equipment: [],
      residentialArea: facility?.residentialArea || false,
      noiseAfter9pm: false,
      commitmentForm: null,
      clearanceFile: null,
      phone: user?.phone || '',
      whatsapp: user?.whatsApp || ''
    });
  } else {
    setBookingData({
      ...bookingData,
      eventType: 'private',
      isSportingActivity: false,
      date: '',
      endDate: '',           // ADD THIS
      purpose: '',           // ADD THIS
      startTime: '08:00',
      endTime: '18:00',
      guests: 1,
      notes: '',
      equipment: [],
      residentialArea: facility?.residentialArea || false,
      noiseAfter9pm: false,
      commitmentForm: null,
      clearanceFile: null,
      phone: user?.phone || '',
      whatsapp: user?.whatsApp || ''
    });
  }
  
  setSelectedFacility(facility);
  setShowBookingModal(true);
  setBookingStep(1);
};
  // Handle booking submission with file upload for dependents
  const handleBookingSubmit = async () => {
    if (!selectedFacility) return;
    
    // Validate approval file for dependent members
    if (requiresApproval && !approvalFile) {
      addNotification('⚠️ Please upload an approval document from your primary member to make this booking.', 'error');
      return;
    }
    
    setLoading(true);
    
    try {
      console.log('🚀 Starting booking submission...');
      console.log('📦 Selected Facility:', selectedFacility);
      console.log('📦 Booking Data:', bookingData);
      console.log('📦 Requires Approval:', requiresApproval);
      console.log('📦 Approval File:', approvalFile);
      
      if (selectedFacility.sportingOnly && bookingData.eventType !== 'sporting') {
        addNotification('⚠️ This facility is for sporting activities only.', 'error');
        setLoading(false);
        return;
      }

      if (bookingData.eventType === 'religious' || bookingData.eventType === 'political') {
        if (!bookingData.clearanceFile) {
          addNotification('⚠️ Religious/Political events require pre-clearance.', 'error');
          setLoading(false);
          return;
        }
      }

      if (bookingData.eventType === 'private' && bookingData.residentialArea) {
        if (!bookingData.commitmentForm) {
          addNotification('⚠️ Private events in residential areas require a commitment form.', 'error');
          setLoading(false);
          return;
        }
      }

      const equipment_items = bookingData.equipment.length > 0 
        ? bookingData.equipment.map(item => ({
            id: item.id,
            name: item.name,
            quantity: item.quantity
          }))
        : null;

      let finalNotes = bookingData.notes;
      if (bookingData.eventType === 'religious' || bookingData.eventType === 'political') {
        finalNotes += '\n\n[Clearance document uploaded]';
      }
      if (bookingData.residentialArea) {
        finalNotes += '\n\nIMPORTANT: Residential area - neighbors must be informed.';
      }

      let response;
      
      if (requiresApproval && approvalFile) {
        // Use FormData for dependent booking with file upload
// In handleBookingSubmit, update the formData or bookingPayload:
const formData = new FormData();
formData.append('booking_type', 'facility');
formData.append('facility_id', selectedFacility.id);
formData.append('facility_name', selectedFacility.name);
formData.append('booking_date', bookingData.date);
formData.append('end_date', bookingData.endDate || '');  // ADD THIS
formData.append('start_time', bookingData.startTime);
formData.append('end_time', bookingData.endTime);
formData.append('purpose', bookingData.purpose || '');   // ADD THIS
formData.append('event_type', bookingData.eventType);
formData.append('guests', bookingData.guests);
formData.append('location', selectedFacility.location);
formData.append('notes', finalNotes);
formData.append('contact_phone', bookingData.phone || user?.phone);
formData.append('contact_email', user?.email);
formData.append('approval_file', approvalFile);



        const token = localStorage.getItem('nlngra_token');
        const API_BASE_URL = process.env.NODE_ENV === 'development' ? '/api' : 'https://nlngraclub.com/nlnapi';
        
        const fetchResponse = await fetch(`${API_BASE_URL}/bookings.php?action=create`, {
          method: 'POST',
          headers: {
            'Authorization': 'Bearer ' + token
          },
          body: formData
        });
        
        const rawText = await fetchResponse.text();
        console.log('📡 Raw response:', rawText);
        
        try {
          response = JSON.parse(rawText);
        } catch (e) {
          const jsonMatch = rawText.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            response = JSON.parse(jsonMatch[0]);
          } else {
            throw new Error('Invalid response from server');
          }
        }
      } else {
        // Use regular JSON for non-dependent bookings
        const bookingPayload = {
          booking_type: 'facility',
          facility_id: selectedFacility.id,
          facility_name: selectedFacility.name,
          booking_date: bookingData.date,
  end_date: bookingData.endDate || '',  // ADD THIS
  start_time: bookingData.startTime,
  end_time: bookingData.endTime,
  purpose: bookingData.purpose || '',   // ADD THIS
          event_type: bookingData.eventType,
          guests: bookingData.guests,
          guests_count: bookingData.guests,
          location: selectedFacility.location,
          special_requirements: bookingData.notes,
          notes: finalNotes,
          contact_phone: bookingData.phone || user?.phone,
          contact_whatsapp: bookingData.whatsapp || user?.whatsApp,
          contact_email: user?.email,
          equipment_items: equipment_items,
          purpose: null,
          borrow_duration: null,
          section_id: null,
          section_name: null,
          jersey_request: null,
          event_id: null,
          previous_location: null
        };
        
        response = await bookingService.createBooking(bookingPayload);
      }

      console.log('📥 Received response:', response);

      if (response && response.success) {
        const bookingNumber = response.booking?.booking_number || response.booking_number || 'submitted';
        
        if (response.requires_approval) {
          addNotification(
            `📄 Dependent booking #${bookingNumber} submitted with approval document. Awaiting admin review.`,
            'info'
          );
        } else {
          addNotification(
            `✅ Booking request #${bookingNumber} submitted successfully!`,
            'success'
          );
        }
        
        if (addAuditLog) {
          addAuditLog('Booking Request Submitted', { 
            requestId: bookingNumber, 
            facility: selectedFacility.name, 
            eventType: bookingData.eventType,
            requiresApproval: requiresApproval
          });
        }
        
        // Reset form data
        setBookingData({
          date: '',
          startTime: '08:00',
          endTime: '18:00',
          guests: 1,
          notes: '',
          equipment: [],
          eventType: 'private',
          residentialArea: false,
          noiseAfter9pm: false,
          commitmentForm: null,
          clearanceFile: null,
          isSportingActivity: false,
          phone: user?.phone || '',
          whatsapp: user?.whatsApp || ''
        });
        setApprovalFile(null);
        setApprovalFileName('');
        setShowBookingModal(false);
      } else {
        const errorMsg = response?.message || response?.error || 'Failed to create booking';
        console.error('❌ Booking failed:', errorMsg);
        addNotification(`❌ ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('🔥 Booking error caught:', error);
      
      let errorMessage = 'An error occurred. Please try again.';
      
      if (error.message.includes('timeout')) {
        errorMessage = 'Request timed out. Please check your connection and try again.';
      } else if (error.message.includes('Network')) {
        errorMessage = 'Network error. Please check your internet connection.';
      } else if (error.message.includes('JSON')) {
        errorMessage = 'Server returned an invalid response. Please contact support.';
      }
      
      addNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleEquipment = (equipment) => {
    const exists = bookingData.equipment.find(e => e.id === equipment.id);
    if (exists) {
      setBookingData({
        ...bookingData,
        equipment: bookingData.equipment.filter(e => e.id !== equipment.id)
      });
    } else {
      setBookingData({
        ...bookingData,
        equipment: [...bookingData.equipment, { ...equipment, quantity: 1 }]
      });
    }
  };

  const updateEquipmentQuantity = (equipmentId, quantity) => {
    setBookingData({
      ...bookingData,
      equipment: bookingData.equipment.map(e => 
        e.id === equipmentId ? { ...e, quantity: Math.max(1, quantity) } : e
      )
    });
  };

  const removeEquipment = (equipmentId) => {
    setBookingData({
      ...bookingData,
      equipment: bookingData.equipment.filter(e => e.id !== equipmentId)
    });
  };

  return (
    <div className="facilities-page">
      <div className="page-header">
        <h2 className="page-title">
          <i className="fa-solid fa-building"></i>
          Facilities & Event Spaces ({facilities.length})
        </h2>
        
        {/* Show equipment loading/error status */}
        {loadingEquipment && (
          <div className="equipment-loading">
            <i className="fa-solid fa-spinner fa-spin"></i>
            <span>Loading equipment catalog...</span>
          </div>
        )}
        
        {equipmentError && (
          <div className="equipment-error">
            <i className="fa-solid fa-exclamation-triangle"></i>
            <span>Equipment catalog: {equipmentError}</span>
          </div>
        )}
        
        {/* Dependent member warning banner */}
        {isDependent && (
          <div className="dependent-warning-banner">
            <i className="fa-solid fa-file-upload"></i>
            <div className="warning-content">
              <strong>Dependent Member Notice:</strong>
              <p>As a dependent member, you need to upload an approval document from your primary member for each booking.</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Category Filter */}
      {allCategories.length > 1 && (
        <div className="category-filters">
          {allCategories.map(cat => (
            <button 
              key={cat} 
              className={`category-filter ${selectedCategory === cat ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat === 'all' ? 'All' : cat}
            </button>
          ))}
        </div>
      )}

      {/* Facilities Grid */}
      <div className="facilities-grid">
        {filteredFacilities.length > 0 ? (
          filteredFacilities.map(facility => {
            const imageUrl = imageErrors[facility.id] 
              ? getFallbackImage(facility.category)
              : getFacilityImage(facility);
            
            return (
              <motion.div 
                key={facility.id}
                className={`facility-card ${facility.sportingOnly ? 'sporting-facility' : ''} ${facility.maintenanceStatus === 'maintenance' ? 'maintenance' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="card-image">
                  <img 
                    src={imageUrl} 
                    alt={facility.name}
                    onError={() => handleImageError(facility.id)}
                  />
                  <div className="card-category">
                    <i className={facility.icon || 'fa-solid fa-building'}></i>
                    {facility.category}
                  </div>
                  {facility.rating && (
                    <div className="card-rating">
                      <i className="fa-solid fa-star"></i>
                      <span>{facility.rating}</span>
                    </div>
                  )}
                  {facility.sportingOnly && (
                    <div className="badge sporting-badge">
                      <i className="fa-solid fa-medal"></i>
                      Sporting Only
                    </div>
                  )}
                  {facility.maintenanceStatus === 'maintenance' && (
                    <div className="badge maintenance-badge">
                      <i className="fa-solid fa-tools"></i>
                      Under Maintenance
                    </div>
                  )}
                </div>
                
                <div className="card-content">
                  <h3 className="card-title">{facility.name}</h3>
                  <p className="card-description">{facility.description}</p>
                  
                  <div className="card-details">
                    <div className="detail">
                      <i className="fa-solid fa-users detail-icon"></i>
                      <span>{facility.capacity || 'Up to 100'}</span>
                    </div>
                    <div className="detail">
                      <i className="fa-regular fa-clock detail-icon"></i>
                      <span>{facility.availability || '8am - 10pm'}</span>
                    </div>
                    <div className="detail">
                      <i className="fa-solid fa-location-dot detail-icon"></i>
                      <span>{facility.location || 'Club Premises'}</span>
                    </div>
                  </div>

                  {facility.size && (
                    <div className="facility-specs">
                      <div className="spec">
                        <i className="fa-solid fa-ruler"></i>
                        <span>{facility.size}</span>
                      </div>
                    </div>
                  )}

                  {facility.amenities && facility.amenities.length > 0 && (
                    <div className="card-amenities">
                      {facility.amenities.slice(0, 4).map((amenity, index) => (
                        <span key={index} className="amenity">
                          <i className="fa-solid fa-check"></i> {amenity}
                        </span>
                      ))}
                      {facility.amenities.length > 4 && (
                        <span className="amenity more">+{facility.amenities.length - 4}</span>
                      )}
                    </div>
                  )}

                  <button 
                    className="book-btn"
                    onClick={() => handleBookNow(facility)}
                    disabled={facility.maintenanceStatus === 'maintenance' || loading}
                  >
                    <i className="fa-solid fa-calendar-plus"></i>
                    {loading ? 'Processing...' : 'Request Booking'}
                  </button>
                </div>
              </motion.div>
            );
          })
        ) : (
          <div className="no-results">
            <i className="fa-solid fa-search"></i>
            <h3>No facilities found</h3>
            <p>Try adjusting your filter</p>
          </div>
        )}
      </div>

      {/* Booking Modal - Pass equipment data from SQL */}
{/* Booking Modal - Pass equipment data from SQL */}
<AnimatePresence>
  {showBookingModal && selectedFacility && (
    <BookingModal
      facility={selectedFacility}
      user={user}
      setUser={setUser}
      bookingData={bookingData}
      setBookingData={setBookingData}
      bookingStep={bookingStep}
      setBookingStep={setBookingStep}
      onClose={() => setShowBookingModal(false)}
      onSubmit={handleBookingSubmit}
      equipmentByCategory={equipmentByCategory}
      toggleEquipment={toggleEquipment}
      updateEquipmentQuantity={updateEquipmentQuantity}
      removeEquipment={removeEquipment}
      loading={loading}
      // Props for dependent approval
      requiresApproval={requiresApproval}
      approvalFile={approvalFile}
      setApprovalFile={setApprovalFile}
      approvalFileName={approvalFileName}
      setApprovalFileName={setApprovalFileName}
      addNotification={addNotification}  // <-- ADD THIS LINE
    />
  )}
</AnimatePresence>
    </div>
  );
};

export default FacilitiesPage;